/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable arrow-body-style */
import { useTranslation } from 'react-i18next'
import { OptionType, TranslationItemModel } from '@/shared/api/BaseAPI.model'
import { useEffect, useRef, useState } from 'react'
import { useMenuTypeFormConfig } from '@/features/menu-management/SidebarAccordion/forms/useMenuTypeFormConfig'
import AccordionContent from '../AccordionContent/AccordionContent'
import { useSelectedLanguagesQuery } from '../../hooks/useSelectedLanguagesQuery'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { MENU_TYPE_HOURS_ENABLED } from '../../config'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useMenuTypesQuery } from '../../hooks/useMenuTypesQuery'
import { useDishCategoriesQuery } from '../../hooks/useDishCategoriesQuery'

interface MenuTypesAccordionProps {
  accordionIndex: number
  openAccordion: number
  handleAccordionClick: (index: number) => void
}

function MenuTypesAccordion(props: MenuTypesAccordionProps) {
  const { accordionIndex, openAccordion, handleAccordionClick } = props
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const initialMenuTypeFormConfig = useMenuTypeFormConfig()
  const [updatedMenuTypeFormConfig, setUpdatedMenuTypeFormConfig] = useState(
    initialMenuTypeFormConfig
  )
  const [menuTypeId, setMenuTypeId] = useState<string>()
  const configuredMenuTypeIdRef = useRef<string>()
  const resetIsDirty = useFormDirtyStore(state => state.reset)
  const { showSnackbar } = useSnackbar()
  const { menuTypesList, menuType, postMenuType, putMenuType, deleteMenuType } =
    useMenuTypesQuery(menuTypeId)
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { selectedLanguagesList, defaultLanguage } =
    useSelectedLanguagesQuery(currentLanguage)
  const { dishCategoriesOptions } = useDishCategoriesQuery()

  const contentList = menuTypesList.data?.map(item => {
    const updatedTranslations = selectedLanguagesList?.map(language => {
      const existingTranslation = item.translations.find(
        translation => translation.languageCode === language.languageCode
      )
      return (
        existingTranslation || {
          name: t('SIDEBAR_ACCORDION.MISSING_TRANSLATION'),
          languageCode: language.languageCode
        }
      )
    })
    return {
      ...item,
      translations: updatedTranslations || []
    }
  })

  // The modal opens only from inside the effect above, on data. A failed load
  // therefore reads as a dead button, nothing opens and nothing is said, and
  // the row cannot even be retried, because `menuTypeId` stays set and clicking
  // the same row again is not a state change.
  useEffect(() => {
    if (!menuType.isError) return
    showSnackbar(
      t('ALERTS.LOAD_FOR_EDIT_ERROR.TITLE'),
      t('ALERTS.LOAD_FOR_EDIT_ERROR.DESCRIPTION'),
      'error'
    )
    configuredMenuTypeIdRef.current = undefined
    setMenuTypeId(undefined)
  }, [menuType.isError, showSnackbar, t])

  // Empty means served all day, which is what every menu type is today, so an
  // untouched pair must travel as null rather than "".
  // Required by the backend on both POST and PUT, and the update replaces the
  // set rather than merging, so an edit that omitted it would silently unassign
  // every category the menu type had.
  const categoryIdsPayload = (data: any): string[] =>
    ((data.categories ?? []) as OptionType[]).map(option => option.value)

  const hoursPayload = (data: any) =>
    MENU_TYPE_HOURS_ENABLED
      ? {
          activeFrom: data.activeFrom || null,
          activeTo: data.activeTo || null
        }
      : {}

  // Declared ahead of the submit handlers that call it, so it can stay an
  // arrow constant instead of a hoisted declaration (react/jsx-no-bind).
  const handleOnCloseModal = () => {
    configuredMenuTypeIdRef.current = undefined
    setMenuTypeId(undefined)
    setIsModalOpen(false)
  }

  const handleAddMenuType = () => {
    const newOnSubmit = (data: any) => {
      const translationsObj = data.translations ?? {}

      const languageCodes = Object.keys(translationsObj)
      const translationStrings = Object.values(translationsObj) as string[]

      const payload = {
        translations: translationStrings.map((str, i) => ({
          name: str,
          languageCode: languageCodes[i]
        })),
        categoryIds: categoryIdsPayload(data),
        ...hoursPayload(data)
      }

      // Closed from the mutation's own success callback so a failed save
      // keeps the form and everything typed into it. Closing here skips
      // AccordionItemModal's close path, the only place that clears the dirty
      // flag, so that is done here too.
      postMenuType(payload, {
        onSuccess: () => {
          resetIsDirty()
          handleOnCloseModal()
        }
      })
    }
    const newFormConfig = {
      ...initialMenuTypeFormConfig,
      // Explicitly empty: spreading the initial config alone would leave the
      // `defaultValues` of the menu type edited before it in place, and the add
      // form would open pre-filled with that menu type.
      defaultValues: undefined,
      onSubmit: newOnSubmit
    }
    setUpdatedMenuTypeFormConfig(newFormConfig)
    setIsModalOpen(true)
  }

  const hadleEditMenuType = (menuItemId: string) => {
    setMenuTypeId(menuItemId)
  }

  useEffect(() => {
    // Keyed on the id rather than on `menuType.data`: react-query's structural
    // sharing hands back the same object when nothing changed, so an effect
    // watching only the data can miss a reopen.
    if (
      menuType.data &&
      menuTypeId &&
      menuTypeId !== configuredMenuTypeIdRef.current
    ) {
      configuredMenuTypeIdRef.current = menuTypeId
      const onSubmit = (data: any) => {
        const languageCodeList: string[] = []
        languageCodeList.push(...Object.keys(data.translations))

        const translationsList: string[] = []
        translationsList.push(...(Object.values(data.translations) as string[]))

        const payload = {
          translations: translationsList.map(
            (translation: string, index): TranslationItemModel => ({
              name: translation,
              languageCode: languageCodeList[index]
            })
          ),
          categoryIds: categoryIdsPayload(data),
          ...hoursPayload(data)
        }
        putMenuType(
          { menuTypeId, menuTypePayload: payload },
          {
            onSuccess: () => {
              resetIsDirty()
              handleOnCloseModal()
            }
          }
        )
      }

      const selectedLanguageListCodeNames = selectedLanguagesList?.map(
        language => language.languageCode
      )

      const menuTypeLanguageList = menuType.data.translations.filter(
        translation =>
          selectedLanguageListCodeNames?.includes(translation.languageCode)
      )

      const defaultLanguageValue =
        menuTypeLanguageList.find(
          defaultLanguage =>
            defaultLanguage.languageCode === defaultLanguage?.languageCode
        )?.name || ''

      const defaultValues = {
        [defaultLanguage?.languageCode || '']: defaultLanguageValue,
        // Mapped back to options rather than ids: the multi-select speaks
        // `{ label, value }`, and an id it cannot find a label for is dropped
        // rather than shown as a blank chip.
        categories: (menuType.data.categoryIds ?? [])
          .map(id => dishCategoriesOptions.find(option => option.value === id))
          .filter((option): option is OptionType => !!option),
        ...(MENU_TYPE_HOURS_ENABLED
          ? {
              activeFrom: menuType.data.activeFrom ?? '',
              activeTo: menuType.data.activeTo ?? ''
            }
          : {}),
        translations: {
          ...menuTypeLanguageList?.reduce((acc: any, translation) => {
            acc[translation.languageCode] = translation.name
            return acc
          }, {})
        }
      }

      const newFormConfig = {
        ...initialMenuTypeFormConfig,
        defaultValues,
        onSubmit
      }
      setUpdatedMenuTypeFormConfig(newFormConfig)
      setIsModalOpen(true)
    }
  }, [menuType.data, menuTypeId, resetIsDirty, dishCategoriesOptions])

  return (
    <AccordionContent
      title={t('SIDEBAR_ACCORDION.MENU_TYPES.TITLE')}
      modalTitle={t('SIDEBAR_ACCORDION.MENU_TYPES.MODAL_TITLE')}
      accordionIndex={accordionIndex}
      openAccordion={openAccordion}
      handleAccordionClick={handleAccordionClick}
      contentList={contentList || []}
      deleteFunction={deleteMenuType}
      deleteMessage={t('SIDEBAR_ACCORDION.MENU_TYPES.DELETE_MESSAGE')}
      loadingItemId={menuType.isLoading ? menuTypeId : undefined}
      accordionItemFormConfig={updatedMenuTypeFormConfig}
      handleAddAccordionItem={handleAddMenuType}
      handleEditAccordionItem={hadleEditMenuType}
      handleOnCloseModal={handleOnCloseModal}
      isModalOpen={isModalOpen}
    />
  )
}
export default MenuTypesAccordion
